import React, { useCallback, useEffect, useState } from 'react';
import useUser from '../../../hooks/useUser';
import { getErrorMessage } from '../../../utils/errorMessage';
import { Alert, Button } from '../../../design-system';
import AccountSettingsLoadingState from '../AccountSettingsLoadingState';
import {
  getAccountSettingsUserCacheKey,
  makeAccountSettingsCacheKey,
  readAccountSettingsCache,
  writeAccountSettingsCache,
} from '../accountSettingsDataCache';
import './UsageSettings.css';

type ModelUsageRow = {
  model: string;
  events: number;
  inputTokens: number;
  outputTokens: number;
  cachedInputTokens: number;
  uncachedInputTokens: number;
  cacheWriteInputTokens: number;
  estimatedCostUsd: number;
};

type UsageSummaryResponse = {
  cycleStartDate: string | null;
  cycleEndDate: string | null;
  limits: {
    maxMessagesPerMonth: number;
    maxStrategyRevisionsPerMonth: number;
    maxBacktestsPerMonth: number;
    maxBacktestMinutesPerMonth: number;
    maxBots: number;
  };
  usage: {
    messagesUsed: number;
    messagesRemaining: number;
    revisionsUsed: number;
    revisionsRemaining: number;
    backtestsUsed: number;
    backtestMinutesUsed: number;
    backtestMinutesRemaining: number | null;
    ai: {
      eventCount: number;
      inputTokens: number;
      outputTokens: number;
      cachedInputTokens: number;
      uncachedInputTokens: number;
      cacheWriteInputTokens: number;
      estimatedCostUsd: number;
      byModel: ModelUsageRow[];
    };
  };
  message?: string;
};

const formatNumber = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return 'n/a';
  return new Intl.NumberFormat('en-US').format(value);
};

const formatUsd = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return 'n/a';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value);
};

const formatLimit = (value: number | null | undefined): string => {
  if (value == null) return 'n/a';
  if (value < 0) return 'Unlimited';
  return formatNumber(value);
};

const formatDate = (iso: string | null): string => {
  if (!iso) return 'n/a';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'n/a';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const UsageSettings = (): React.ReactElement => {
  const { getApiToken, isAuthenticated, authLoading, user } = useUser();
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'api';
  const cacheKey = makeAccountSettingsCacheKey(
    'usage-summary',
    getAccountSettingsUserCacheKey(user),
    apiBaseUrl,
  );
  const cached = readAccountSettingsCache<UsageSummaryResponse>(cacheKey);
  const [summary, setSummary] = useState<UsageSummaryResponse | null>(() => cached || null);
  const [loading, setLoading] = useState(() => !cached);
  const [error, setError] = useState('');

  const loadUsage = useCallback(async (options: { force?: boolean } = {}) => {
    if (!isAuthenticated || authLoading) return;
    if (!options.force) {
      const existing = readAccountSettingsCache<UsageSummaryResponse>(cacheKey);
      if (existing) {
        setSummary(existing);
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    setError('');
    try {
      const token = await getApiToken();
      const response = await fetch(`${apiBaseUrl}/agent/usage-summary`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = (await response.json()) as UsageSummaryResponse;
      if (!response.ok) {
        throw new Error(data.message || 'Could not load usage summary.');
      }
      writeAccountSettingsCache(cacheKey, data);
      setSummary(data);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not load usage summary.'));
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, authLoading, cacheKey, getApiToken, isAuthenticated]);

  useEffect(() => {
    void loadUsage();
  }, [loadUsage]);

  if (authLoading || (loading && !summary)) {
    return (
      <AccountSettingsLoadingState
        label="Usage"
        text="Loading this billing cycle."
      />
    );
  }

  const usage = summary?.usage;
  const limits = summary?.limits;
  const ai = usage?.ai;

  return (
    <div className="usage-settings" data-testid="usage-settings">
      <div className="settings-subpage-header">
        <div className="settings-subpage-header-left">
          <h2 className="settings-subpage-title">Usage</h2>
          <p className="settings-subpage-description">
            This billing cycle. Resets {formatDate(summary?.cycleEndDate ?? null)}.
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => {
            void loadUsage({ force: true });
          }}
        >
          Refresh
        </Button>
      </div>

      {error ? (
        <Alert variant="danger" className="usage-settings-error">
          {error}
        </Alert>
      ) : null}

      <section className="usage-metric-grid" aria-label="Plan usage">
        <div className="usage-metric">
          <span className="usage-metric-label">AI spend (est.)</span>
          <strong className="usage-metric-value">{formatUsd(ai?.estimatedCostUsd)}</strong>
        </div>
        <div className="usage-metric">
          <span className="usage-metric-label">Messages</span>
          <strong className="usage-metric-value">
            {formatNumber(usage?.messagesUsed)} / {formatLimit(limits?.maxMessagesPerMonth)}
          </strong>
        </div>
        <div className="usage-metric">
          <span className="usage-metric-label">Revisions</span>
          <strong className="usage-metric-value">
            {formatNumber(usage?.revisionsUsed)} / {formatLimit(limits?.maxStrategyRevisionsPerMonth)}
          </strong>
        </div>
        <div className="usage-metric">
          <span className="usage-metric-label">Backtest minutes</span>
          <strong className="usage-metric-value">
            {formatNumber(usage?.backtestMinutesUsed)} / {formatLimit(limits?.maxBacktestMinutesPerMonth)}
          </strong>
        </div>
      </section>

      <section className="usage-token-block" aria-label="Token usage">
        <h3 className="usage-section-title">Tokens</h3>
        <dl className="usage-token-list">
          <div>
            <dt>Input</dt>
            <dd>{formatNumber(ai?.inputTokens)}</dd>
          </div>
          <div>
            <dt>Output</dt>
            <dd>{formatNumber(ai?.outputTokens)}</dd>
          </div>
          <div>
            <dt>Cached input</dt>
            <dd>{formatNumber(ai?.cachedInputTokens)}</dd>
          </div>
          <div>
            <dt>Uncached input</dt>
            <dd>{formatNumber(ai?.uncachedInputTokens)}</dd>
          </div>
          <div>
            <dt>Cache write</dt>
            <dd>{formatNumber(ai?.cacheWriteInputTokens)}</dd>
          </div>
          <div>
            <dt>AI events</dt>
            <dd>{formatNumber(ai?.eventCount)}</dd>
          </div>
        </dl>
      </section>

      <section className="usage-model-block" aria-label="Usage by model">
        <h3 className="usage-section-title">By model</h3>
        {!ai?.byModel?.length ? (
          <p className="usage-empty">No AI usage recorded in this cycle yet.</p>
        ) : (
          <div className="usage-model-table-wrap">
            <table className="usage-model-table">
              <thead>
                <tr>
                  <th scope="col">Model</th>
                  <th scope="col">Events</th>
                  <th scope="col">Input</th>
                  <th scope="col">Output</th>
                  <th scope="col">Cached</th>
                  <th scope="col">Est. $</th>
                </tr>
              </thead>
              <tbody>
                {ai.byModel.map((row) => (
                  <tr key={row.model}>
                    <td>{row.model}</td>
                    <td>{formatNumber(row.events)}</td>
                    <td>{formatNumber(row.inputTokens)}</td>
                    <td>{formatNumber(row.outputTokens)}</td>
                    <td>{formatNumber(row.cachedInputTokens)}</td>
                    <td>{formatUsd(row.estimatedCostUsd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p className="usage-cycle-note">
        Cycle {formatDate(summary?.cycleStartDate ?? null)} to {formatDate(summary?.cycleEndDate ?? null)}.
        Estimated AI spend uses provider list prices plus BotSpot markup.
      </p>
    </div>
  );
};

export default UsageSettings;
